import {
  FormSubmissionData,
  FormConfiguration
} from '../models/forms/form-models';
import {
  EnrollmentType,
  EnrollmentPayload,
  EnrollmentResponse
} from '../models/enrollment/enrollment';
import { ValidationService } from './validation-service';

/**
 * API Service Interface (Dependency Inversion Principle)
 */
interface IApiService {
  post<T>(endpoint: string, data: any): Promise<T>;
}

/**
 * Mock API Service Implementation
 */
class MockApiService implements IApiService {
  async post<T>(endpoint: string, data: any): Promise<T> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Simulate random success/failure (85% success rate)
    const isSuccess = Math.random() > 0.15;

    if (isSuccess) {
      return {
        success: true,
        message: this.getSuccessMessage(data.type),
        enrollmentId: `${data.type.toUpperCase()}-${data.entityId}-${Date.now()}`
      } as T;
    } else {
      throw new Error('We are currently experiencing high enrollment volume. Please try again in a few minutes.');
    }
  }

  private getSuccessMessage(type: EnrollmentType): string {
    switch (type) {
      case EnrollmentType.MINISTRY:
        return 'Your ministry enrollment has been submitted successfully! We will contact you soon with next steps.';
      case EnrollmentType.SCHOOL:
        return 'Your school enrollment has been submitted successfully! You will receive enrollment details shortly.';
      case EnrollmentType.EVENT:
        return 'Your event registration has been submitted successfully! We look forward to seeing you there.';
      default:
        return 'Your enrollment has been submitted successfully!';
    }
  }
}

/**
 * Generic Enrollment Service following SOLID principles
 */
export class EnrollmentService {
  private apiService: IApiService;

  constructor(apiService?: IApiService) {
    // Dependency injection - can be replaced with real API service later
    this.apiService = apiService || new MockApiService();
  }

  /**
   * Generic enrollment submission method
   */
  async submitEnrollment<T extends FormSubmissionData>(
    type: EnrollmentType,
    entityId: number,
    formData: T,
    formConfig: FormConfiguration
  ): Promise<EnrollmentResponse> {
    try {
      // Step 1: Validate form data
      const validationResult = ValidationService.validateForm(formData, formConfig.sections);

      if (!validationResult.isValid) {
        return {
          success: false,
          message: 'Please correct the following errors',
          error: 'Validation failed',
          validationErrors: validationResult.errors
        };
      }

      // Step 2: Sanitize and prepare payload
      const sanitizedData = ValidationService.sanitizeFormData(formData);
      const payload: EnrollmentPayload<T> = {
        type,
        entityId,
        formData: sanitizedData as T,
        submittedAt: new Date()
      };

      // Step 3: Submit to API
      const response = await this.apiService.post<EnrollmentResponse>('/api/enrollment', payload);
      return response;

    } catch (error) {
      return {
        success: false,
        message: 'Enrollment failed',
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  /**
   * Ministry-specific enrollment method for backward compatibility
   */
  async submitMinistryEnrollment(
    ministryId: number,
    formData: FormSubmissionData,
    formConfig: FormConfiguration
  ): Promise<EnrollmentResponse> {
    return this.submitEnrollment(EnrollmentType.MINISTRY, ministryId, formData, formConfig);
  }

  /**
   * School-specific enrollment method
   */
  async submitSchoolEnrollment(
    schoolId: number,
    formData: FormSubmissionData,
    formConfig: FormConfiguration
  ): Promise<EnrollmentResponse> {
    return this.submitEnrollment(EnrollmentType.SCHOOL, schoolId, formData, formConfig);
  }

  /**
   * Event-specific enrollment method
   */
  async submitEventEnrollment(
    eventId: number,
    formData: FormSubmissionData,
    formConfig: FormConfiguration
  ): Promise<EnrollmentResponse> {
    return this.submitEnrollment(EnrollmentType.EVENT, eventId, formData, formConfig);
  }
}

// Export singleton instance
export const enrollmentService = new EnrollmentService();

// Legacy interface for backward compatibility
export interface EnrollmentFormData {
  fullName: string;
  email: string;
  phoneNumber: string;
  ageRange: string;
  churchExperience: string;
  motivation: string;
  agreement: boolean;
}
